import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Globe, EyeOff, Eye, UserCog, AlertCircle, Users } from 'lucide-react';
import {
  adminGetTeamPlayerDirectory, adminSetTeamPublicRoster,
  adminUpdatePlayerProfileStatus, adminUpdatePlayerProfileVisibility,
  friendlyError,
} from '@/lib/api';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]',
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]',
  hidden: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-subtle)]',
  disabled: 'bg-[var(--ns-red)]/15 text-[var(--ns-red)]',
};
const STATUS_LABEL = {
  draft: 'Borrador', pending_review: 'Pendiente revisión',
  approved: 'Aprobado', hidden: 'Oculto', disabled: 'Desactivado',
};
const SCOPE_LABEL = {
  none: 'No visible', team_card: 'Tarjeta básica', full_profile_and_highlights: 'Perfil completo',
};

export default function AdminTeamDirectory() {
  const { clubId, teamId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [updating, setUpdating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminGetTeamPlayerDirectory(clubId, teamId);
      setData(d);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clubId, teamId]);

  const togglePublicRoster = async (val) => {
    setUpdating(true);
    try {
      const res = await adminSetTeamPublicRoster(teamId, val);
      toast.success(res.message || 'Actualizado.');
      await load();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setUpdating(false);
    }
  };

  const setProfileStatus = async (pid, status) => {
    try { await adminUpdatePlayerProfileStatus(pid, status); toast.success('Estado actualizado.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };
  const setProfileScope = async (pid, scope) => {
    try { await adminUpdatePlayerProfileVisibility(pid, { external_visibility_scope: scope }); toast.success('Visibilidad actualizada.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <Skeleton className="h-12 w-64" />
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      </AdminLayout>
    );
  }
  if (!data) {
    return (
      <AdminLayout>
        <Card className="p-8 text-center">
          <p className="text-sm text-[var(--ns-text-muted)]">No se ha podido cargar el directorio.</p>
        </Card>
      </AdminLayout>
    );
  }

  const { club, team, items, total } = data;
  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <div>
          <Link to="/admin/fichas-jugadores" className="inline-flex items-center gap-1 text-sm text-[var(--ns-text-muted)] hover:text-[var(--ns-gold-700)]">
            <ArrowLeft className="h-4 w-4" /> Volver a Fichas de jugadores
          </Link>
        </div>

        <header>
          <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl text-[var(--ns-text)]">
            Directorio · {team?.name}
          </h1>
          <p className="text-sm text-[var(--ns-text-muted)] mt-1">
            {club?.name} · {team?.category} · {total} {total === 1 ? 'jugador' : 'jugadores'} en este equipo.
          </p>
        </header>

        <Card data-testid="admin-team-public-roster-card" className="p-4 rounded-2xl bg-white border border-[var(--ns-border)] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[var(--ns-amber-50)] text-[var(--ns-gold-700)] flex items-center justify-center">
              <Globe className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[var(--ns-text)]">Plantilla pública del equipo</p>
              <p className="text-xs text-[var(--ns-text-muted)]">
                Si está desactivada, ningún jugador del equipo será visible públicamente, aunque tenga visibilidad individual.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--ns-text-muted)]">{team?.public_roster_enabled ? 'Activa' : 'Inactiva'}</span>
            <Switch
              checked={!!team?.public_roster_enabled}
              disabled={updating}
              onCheckedChange={togglePublicRoster}
              data-testid="admin-team-public-roster-toggle"
            />
          </div>
        </Card>

        {total === 0 ? (
          <Card className="p-8 rounded-2xl bg-white border border-dashed border-[var(--ns-border-strong)] text-center">
            <Users className="h-10 w-10 text-[var(--ns-text-subtle)] mx-auto mb-3" />
            <p className="text-sm text-[var(--ns-text-muted)]">
              Este equipo todavía no tiene jugadores vinculados.
            </p>
          </Card>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => {
              const avatar = resolvePlayerAvatar({ profile_photo: p.profile_photo, gender: 'not_specified' });
              return (
                <li key={p.id} data-testid={`admin-directory-row-${p.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[var(--ns-border)]">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-12 w-12 rounded-xl overflow-hidden bg-[var(--ns-bg-2)] flex items-center justify-center shrink-0">
                      {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-[var(--ns-text-subtle)]">—</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{p.first_name} {p.last_name}</p>
                      <p className="text-xs text-[var(--ns-text-muted)] truncate">
                        {p.age != null ? `${p.age} años · ` : ''}{p.position || '—'}
                      </p>
                      {!p.is_complete ? (
                        <p className="text-[10px] mt-0.5 inline-flex items-center gap-1 text-amber-700 font-semibold">
                          <AlertCircle className="h-3 w-3" />
                          Ficha incompleta (faltan datos obligatorios)
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={`text-[10px] uppercase ${STATUS_COLORS[p.status] || ''}`}>{STATUS_LABEL[p.status] || p.status}</Badge>
                    <Badge variant="outline" className="text-[10px] uppercase">{SCOPE_LABEL[p.external_visibility_scope] || p.external_visibility_scope || '—'}</Badge>
                    <Button asChild size="sm" variant="outline" className="h-8 rounded-lg text-xs">
                      <Link to={`/admin/fichas-jugadores/${p.id}`}>
                        <UserCog className="h-3.5 w-3.5 mr-1" /> Editar
                      </Link>
                    </Button>
                    {p.status === 'hidden' ? (
                      <Button size="sm" variant="outline" data-testid={`admin-directory-show-${p.id}`} className="h-8 rounded-lg text-xs" onClick={() => setProfileStatus(p.id, 'approved')}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> Mostrar
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`admin-directory-hide-${p.id}`} className="h-8 rounded-lg text-xs" onClick={() => setProfileStatus(p.id, 'hidden')}>
                        <EyeOff className="h-3.5 w-3.5 mr-1" /> Ocultar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`admin-directory-scope-card-${p.id}`} className="h-8 rounded-lg text-xs" onClick={() => setProfileScope(p.id, 'team_card')}>Tarjeta</Button>
                    <Button size="sm" variant="outline" data-testid={`admin-directory-scope-full-${p.id}`} className="h-8 rounded-lg text-xs" onClick={() => setProfileScope(p.id, 'full_profile_and_highlights')}>Ficha</Button>
                    <Button size="sm" variant="outline" data-testid={`admin-directory-scope-none-${p.id}`} className="h-8 rounded-lg text-xs" onClick={() => setProfileScope(p.id, 'none')}>Sin visibilidad</Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
